import { fetchWithSupabaseAuth, QA_API_DOMAIN } from "@/api/classifier";
import { z } from "zod";

export interface ErroredWriteBack {
  id: string;
  status: string;
  errorMessage: string | null;
  humanReviewId: string | null;
  inboundClassificationResultId: string | null;
  writtenToS3At: string | null;
  s3Key: string | null;
}

export interface EncounterWriteBackGroup {
  encounterId: string;
  dischargeDate: string | null;
  specialty: string;
  customer: {
    id: string;
    label: string;
  };
  mrn: string | null;
  isRecode: boolean;
  writeBacks: ErroredWriteBack[];
}

export type ErroredEncounterGroup = EncounterWriteBackGroup;

export async function getEncounterWriteBacks(encounterId: string): Promise<EncounterWriteBackGroup | null> {
  const res = await fetchWithSupabaseAuth(
    `${QA_API_DOMAIN}/write-back/admin/encounters/${encodeURIComponent(encounterId)}`,
  );

  if (res.status === 404) return null;
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Failed to fetch encounter write-backs (${res.status})`);
  }

  return res.json() as Promise<EncounterWriteBackGroup>;
}

export interface GetErroredEncountersOptions {
  customerIds?: string[];
  dischargeDateFrom?: string;
  dischargeDateTo?: string;
}

export async function getErroredEncounters(options?: GetErroredEncountersOptions): Promise<ErroredEncounterGroup[]> {
  const params = new URLSearchParams();
  if (options?.customerIds?.length) {
    params.set("customer_ids", options.customerIds.join(","));
  }
  if (options?.dischargeDateFrom) {
    params.set("discharge_date_from", options.dischargeDateFrom);
  }
  if (options?.dischargeDateTo) {
    params.set("discharge_date_to", options.dischargeDateTo);
  }

  const url = `${QA_API_DOMAIN}/write-back/admin/errored-encounters${params.toString() ? `?${params}` : ""}`;
  const res = await fetchWithSupabaseAuth(url, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Failed to fetch errored encounters (${res.status})`);
  }

  return res.json() as Promise<ErroredEncounterGroup[]>;
}

// `deferred` is a 202 from qa-api: the HRG sweep has not grouped these codes yet and the next
// sweep sends them. Nothing failed, so it toasts rather than throwing.
const KNOWN_WRITE_BACK_SEND_OUTCOMES = ["sent", "unchanged", "deferred"] as const;
type KnownWriteBackSendOutcome = (typeof KNOWN_WRITE_BACK_SEND_OUTCOMES)[number];

function isKnownWriteBackSendOutcome(value: unknown): value is KnownWriteBackSendOutcome {
  return (KNOWN_WRITE_BACK_SEND_OUTCOMES as readonly unknown[]).includes(value);
}

const WriteBackSendOutcomeSchema = z
  .unknown()
  .optional()
  .transform((value): SendOutcomeKind => {
    if (value === undefined) return "sent";
    if (isKnownWriteBackSendOutcome(value)) return value;
    return "unrecognised";
  });

export type SendOutcomeKind = KnownWriteBackSendOutcome | "unrecognised";

export const WRITE_BACK_SEND_TOAST_KIND: Record<SendOutcomeKind, "success" | "info"> = {
  sent: "success",
  unchanged: "info",
  deferred: "info",
  unrecognised: "info",
};

export const WriteBackSendResultSchema = z.object({
  message: z.string(),
  outcome: WriteBackSendOutcomeSchema,
});

export type WriteBackSendResult = z.infer<typeof WriteBackSendResultSchema>;

export interface RetryResult {
  retried: number;
  unchanged: number;
  failed: number;
  errors: Array<{ encounterId: string; error: string }>;
}

export async function retryErroredEncounters(options?: GetErroredEncountersOptions): Promise<RetryResult> {
  const params = new URLSearchParams();
  if (options?.customerIds?.length) {
    params.set("customer_ids", options.customerIds.join(","));
  }
  if (options?.dischargeDateFrom) {
    params.set("discharge_date_from", options.dischargeDateFrom);
  }
  if (options?.dischargeDateTo) {
    params.set("discharge_date_to", options.dischargeDateTo);
  }

  const url = `${QA_API_DOMAIN}/write-back/admin/retry-errored${params.toString() ? `?${params}` : ""}`;
  const res = await fetchWithSupabaseAuth(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Failed to retry errored encounters (${res.status})`);
  }

  return res.json() as Promise<RetryResult>;
}

const RetryEncounterResultSchema = z.object({
  message: z.string(),
  encounterId: z.string(),
  outcome: WriteBackSendOutcomeSchema,
});

export type RetryEncounterResult = z.infer<typeof RetryEncounterResultSchema>;

export async function retrySingleEncounter(encounterId: string): Promise<RetryEncounterResult> {
  const url = `${QA_API_DOMAIN}/write-back/admin/retry-encounter`;
  const res = await fetchWithSupabaseAuth(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ encounter_id: encounterId }),
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Failed to retry encounter (${res.status})`);
  }

  return RetryEncounterResultSchema.parse(await res.json());
}

export async function markWriteBackSuccess(encounterId: string): Promise<{ message: string; updatedCount: number }> {
  const url = `${QA_API_DOMAIN}/write-back/admin/mark-success`;
  const res = await fetchWithSupabaseAuth(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ encounter_id: encounterId }),
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Failed to mark write-back as succeeded (${res.status})`);
  }

  return (await res.json()) as { message: string; updatedCount: number };
}

export async function markWriteBackSkipped(writeBackId: string): Promise<void> {
  const url = `${QA_API_DOMAIN}/write-back/admin/mark-skipped`;
  const res = await fetchWithSupabaseAuth(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ write_back_id: writeBackId }),
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Failed to mark write-back as skipped (${res.status})`);
  }
}
